import { PERMISSIONS } from '@repo/contracts';
import { lazy } from 'react';
import type { RouteObject } from 'react-router';

import { AppShell } from '@/app/app-shell';
import { APP_ROUTES } from '@/constants/app-routes';
import { adminRoutes } from '@/pages/admin/admin-routes';

import { RequireAuth } from './require-auth';
import { RequirePermission } from './require-permission';

const LoginPage = lazy(() => import('@/pages/login/LoginPage'));
const PortalLayout = lazy(() => import('@/pages/portal/PortalLayout'));
const PortalPage = lazy(() => import('@/pages/portal/PortalPage'));
const NotFoundPage = lazy(() => import('@/pages/not-found/NotFoundPage'));
const RouteErrorPage = lazy(() => import('@/pages/route-error/RouteErrorPage'));
const HomeRoute = lazy(() => import('@/pages/home/HomeRoute'));
const CandidatesPage = lazy(() => import('@/pages/candidates/CandidatesPage'));
const CandidateCreatePage = lazy(() => import('@/pages/candidates/CandidateCreatePage'));
const CandidateDetailPage = lazy(() => import('@/pages/candidates/CandidateDetailPage'));
const OrphanedCandidatesPage = lazy(() => import('@/pages/candidates/OrphanedCandidatesPage'));
const CandidateEditPage = lazy(() => import('@/pages/candidates/CandidateEditPage'));
const EmployeesPage = lazy(() => import('@/pages/employees/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('@/pages/employees/EmployeeDetailPage'));
const DirectNdaListPage = lazy(() => import('@/pages/direct-nda/DirectNdaListPage'));
const DirectNdaDetailPage = lazy(() => import('@/pages/direct-nda/DirectNdaDetailPage'));

export const routes: RouteObject[] = [
  { path: APP_ROUTES.login, element: <LoginPage />, errorElement: <RouteErrorPage /> },
  // Outside RequireAuth on purpose: a candidate has no session, only a token.
  {
    path: APP_ROUTES.portal,
    element: <PortalLayout />,
    errorElement: <RouteErrorPage />,
    children: [{ index: true, element: <PortalPage /> }],
  },
  {
    element: <RequireAuth />,
    // Last resort: only reached if AppShell itself fails to render, so it
    // renders RouteErrorPage without the sidebar chrome that just broke.
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            // A pathless layout route so the boundary sits inside AppShell:
            // an error here keeps the sidebar, instead of errorElement on
            // AppShell itself replacing the whole shell.
            errorElement: <RouteErrorPage />,
            children: [
              { path: APP_ROUTES.home, element: <HomeRoute /> },
              ...adminRoutes,
              {
                element: <RequirePermission permission={PERMISSIONS.candidateRead} />,
                children: [
                  { path: APP_ROUTES.candidates.list, element: <CandidatesPage /> },
                  {
                    path: APP_ROUTES.candidates.detail(':idOrReference'),
                    element: <CandidateDetailPage />,
                  },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.employeeRead} />,
                children: [
                  { path: APP_ROUTES.employees.list, element: <EmployeesPage /> },
                  {
                    path: APP_ROUTES.employees.detail(':reference'),
                    element: <EmployeeDetailPage />,
                  },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.directNdaRead} />,
                children: [
                  { path: APP_ROUTES.directNda.list, element: <DirectNdaListPage /> },
                  {
                    path: APP_ROUTES.directNda.detail(':id'),
                    element: <DirectNdaDetailPage />,
                  },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.candidateCreate} />,
                // Before the `:idOrReference` route in matching order: React Router ranks static
                // segments above dynamic ones, so `new` is never read as a reference.
                children: [
                  { path: APP_ROUTES.candidates.create, element: <CandidateCreatePage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.candidateAssign} />,
                // A static segment, so `orphaned` is never read as a candidate reference.
                children: [
                  { path: APP_ROUTES.candidates.orphaned, element: <OrphanedCandidatesPage /> },
                ],
              },
              {
                element: <RequirePermission permission={PERMISSIONS.candidateUpdate} />,
                children: [
                  {
                    path: APP_ROUTES.candidates.edit(':idOrReference'),
                    element: <CandidateEditPage />,
                  },
                ],
              },
            ],
          },
          // Last child, so it never shadows a real route above it.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];
